import { Button } from '@mui/joy';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useEvent from '../hooks/useEvent';
import useEventPosts from '../hooks/useEventPosts';
import { useUser } from '../hooks/useUser';
import { joinEvent, leaveEvent } from '../redux/actions/eventActions';
import Modal from './modal';

type Props = {
  eventId: string;
};

function JoinEventButton(props: Props) {
  const { eventId } = props;
  const { t } = useTranslation();
  const userQuery = useUser();
  const [loading, setLoading] = useState(false);
  const [leaveModalOpen, setLeaveModalOpen] = useState(false);
  const eventQuery = useEvent(eventId);
  const eventPostsQuery = useEventPosts(eventId);

  const join = async () => {
    setLoading(true);
    await joinEvent(eventId);
    await eventQuery.refetch();
    await eventPostsQuery.refetch();
    setLoading(false);
  };

  const leave = async () => {
    setLoading(true);
    await leaveEvent(eventId);
    await eventQuery.refetch();
    await eventPostsQuery.refetch();
    setLoading(false);
  };

  const event = eventQuery.data;

  const hasJoined = useMemo(() => {
    if (!userQuery.isSuccess || !eventQuery.isSuccess) {
      return false;
    }
    return event?.attendees?.some((member: any) => {
      return member._id === userQuery.data._id;
    });
  }, [eventQuery.data, userQuery.data]);

  if (!eventId) {
    return null;
  }

  if (!eventQuery.isSuccess || !userQuery.isSuccess) {
    return null;
  }

  const isFull =
    eventQuery.data.attendees.length >= eventQuery.data.maxAttendees &&
    eventQuery.data.maxAttendees !== -1;

  return (
    <React.Fragment>
      <Modal
        isOpen={leaveModalOpen}
        onConfirm={async () => {
          await leave();
          return true;
        }}
        setOpen={setLeaveModalOpen}
        showCancel={true}
        title="Afmelden voor evenement?"
        confirmText="Afmelden"
      >
        {null}
      </Modal>
      <Button
        disabled={isFull && !hasJoined}
        loading={loading}
        variant={hasJoined ? 'soft' : 'solid'}
        color={hasJoined ? 'neutral' : 'primary'}
        onClick={() => {
          if (hasJoined) {
            setLeaveModalOpen(true);
          } else {
            join();
          }
        }}
      >
        {hasJoined
          ? t('events.button_leave_event')
          : t('events.button_join_event')}
      </Button>
    </React.Fragment>
  );
}

export default JoinEventButton;
